import React, { useEffect, useMemo, useState } from 'react';
import { Button, Card, DatePicker, Drawer, Form, Input, Layout, Modal, Select, Space, Spin, Table, Tag, Typography, message } from 'antd';
import { DeleteOutlined, EditOutlined, EyeOutlined, PlusOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useDispatch, useSelector } from 'react-redux';
import employeeService from '../services/employeeService';
import { createDocument, deleteDocument, fetchDocuments, updateDocument } from '../redux/slices/documentsSlice';
import { canManageDocuments, isEmployee } from '../utils/permissions';

const ALLOWED_TYPES = ['application/pdf', 'image/jpeg', 'image/png'];

const fileLabel = (mimeType = '') => {
  if (mimeType === 'application/pdf') return 'PDF';
  if (mimeType === 'image/jpeg') return 'JPG';
  if (mimeType === 'image/png') return 'PNG';
  return mimeType || '-';
};

function Documents() {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const { list: documents, loading, pagination } = useSelector((state) => state.documents);
  const [employees, setEmployees] = useState([]);
  const [employeesLoading, setEmployeesLoading] = useState(false);
  const [currentEmployeeId, setCurrentEmployeeId] = useState(user?.employee?.id || null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [viewOpen, setViewOpen] = useState(false);
  const [viewData, setViewData] = useState(null);

  const isEmployeeUser = isEmployee(user);
  const canManage = canManageDocuments(user);
  const shouldScopeToSelf = isEmployeeUser || (currentEmployeeId && !canManage);

  useEffect(() => {
    const hydrateCurrentEmployee = async () => {
      if (!isEmployeeUser) {
        setCurrentEmployeeId(user?.employee?.id || null);
        return;
      }

      if (user?.employee?.id) {
        setCurrentEmployeeId(user.employee.id);
        return;
      }

      try {
        const response = await employeeService.getCurrent();
        setCurrentEmployeeId(response.data?.id || response.data?.data?.id || null);
      } catch (error) {
        setCurrentEmployeeId(null);
      }
    };

    hydrateCurrentEmployee();
  }, [isEmployeeUser, user?.employee?.id]);

  useEffect(() => {
    if (!user) {
      return;
    }

    loadDocuments();

    if (!shouldScopeToSelf) {
      loadEmployees();
    }
  }, [user?.id, currentEmployeeId, shouldScopeToSelf]);

  const loadDocuments = async (page = 1, limit = 10) => {
    try {
      await dispatch(fetchDocuments({
        page,
        limit,
        filters: {
          organizationId: user?.organizationId,
          ...(shouldScopeToSelf && currentEmployeeId ? { employeeId: currentEmployeeId } : {})
        }
      })).unwrap();
    } catch (error) {
      message.error(error || 'Failed to load documents');
    }
  };

  const loadEmployees = async () => {
    setEmployeesLoading(true);
    try {
      const response = await employeeService.getAll(1, 1000, { organizationId: user?.organizationId });
      const employeeList = response?.data?.data || response?.data || [];
      setEmployees(employeeList);
    } catch (error) {
      message.error('Failed to load employees');
    } finally {
      setEmployeesLoading(false);
    }
  };

  const openCreateModal = () => {
    setIsEditMode(false);
    setSelectedDocument(null);
    setSelectedFile(null);
    form.resetFields();
    form.setFieldsValue({
      employeeId: shouldScopeToSelf ? currentEmployeeId : undefined
    });
    setIsModalOpen(true);
  };

  const openEditModal = (record) => {
    setIsEditMode(true);
    setSelectedDocument(record);
    setSelectedFile(null);
    form.setFieldsValue({
      employeeId: record.employeeId,
      name: record.name
    });
    setIsModalOpen(true);
  };

  const submitDocument = async (values) => {
    if (!selectedFile && !isEditMode) {
      message.error('Please select a document file');
      return;
    }

    const payload = {
      ...values,
      file: selectedFile || undefined,
      organizationId: user?.organizationId,
      employeeId: shouldScopeToSelf ? currentEmployeeId : values.employeeId
    };

    try {
      if (isEditMode && selectedDocument?.id) {
        await dispatch(updateDocument({ id: selectedDocument.id, data: payload })).unwrap();
        message.success('Document updated successfully');
      } else {
        await dispatch(createDocument(payload)).unwrap();
        message.success('Document uploaded successfully');
      }

      setIsModalOpen(false);
      setSelectedFile(null);
      form.resetFields();
      loadDocuments();
    } catch (error) {
      message.error(error || 'Failed to save document');
    }
  };

  const handleDelete = (id) => {
    Modal.confirm({
      title: 'Delete Document',
      content: 'Are you sure you want to delete this document?',
      okText: 'Yes',
      cancelText: 'No',
      onOk: async () => {
        try {
          await dispatch(deleteDocument(id)).unwrap();
          message.success('Document deleted successfully');
          loadDocuments();
        } catch (error) {
          message.error(error || 'Failed to delete document');
        }
      }
    });
  };

  const openView = (record) => {
    setViewData(record);
    setViewOpen(true);
  };

  const columns = useMemo(() => [
    {
      title: 'Employee',
      key: 'employee',
      render: (_, record) => `${record.employee?.firstName || ''} ${record.employee?.lastName || ''}`.trim() || '-'
    },
    {
      title: 'Document Name',
      dataIndex: 'name',
      key: 'name'
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      render: (value) => fileLabel(value)
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (value) => value ? dayjs(value).format('DD MMM YYYY HH:mm') : '-'
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button type="text" icon={<EyeOutlined />} onClick={() => openView(record)} />
          {canManage && (
            <>
              <Button type="text" icon={<EditOutlined />} onClick={() => openEditModal(record)} />
              <Button type="text" danger icon={<DeleteOutlined />} onClick={() => handleDelete(record.id)} />
            </>
          )}
        </Space>
      )
    }
  ], [canManage]);

  return (
    <Layout style={{ padding: 20 }}>
      <Space direction="vertical" size={20} style={{ width: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <Typography.Title level={2} style={{ marginBottom: 4 }}>
              {isEmployeeUser ? 'My Documents' : 'Employee Documents'}
            </Typography.Title>
            <Typography.Text type="secondary">
              Upload and manage PDF, JPG, or PNG employee documents.
            </Typography.Text>
          </div>

          <Button type="primary" icon={<PlusOutlined />} onClick={openCreateModal}>
            Add Document
          </Button>
        </div>

        <Spin spinning={loading}>
          <Table
            rowKey="id"
            dataSource={documents}
            columns={columns}
            pagination={{
              current: pagination.page,
              pageSize: pagination.limit,
              total: pagination.total,
              showSizeChanger: true,
              showTotal: (total) => `Total ${total} documents`
            }}
            onChange={(page) => loadDocuments(page.current, page.pageSize)}
          />
        </Spin>
      </Space>

      <Modal
        title={isEditMode ? 'Edit Document' : 'Add Document'}
        open={isModalOpen}
        onCancel={() => {
          setIsModalOpen(false);
          setSelectedFile(null);
          form.resetFields();
        }}
        onOk={() => form.submit()}
        confirmLoading={loading}
      >
        <Form form={form} layout="vertical" onFinish={submitDocument}>
          {!shouldScopeToSelf && (
            <Form.Item
              label="Employee"
              name="employeeId"
              rules={[{ required: true, message: 'Please select an employee' }]}
            >
              <Select
                placeholder="Select employee"
                loading={employeesLoading}
                options={employees.map((employee) => ({
                  label: `${employee.firstName} ${employee.lastName} (${employee.employeeCode})`,
                  value: employee.id
                }))}
              />
            </Form.Item>
          )}

          <Form.Item
            label="Document Name"
            name="name"
            rules={[{ required: true, message: 'Please enter document name' }]}
          >
            <Input placeholder="e.g. PAN Card" />
          </Form.Item>

          <Form.Item label="File">
            <input
              type="file"
              accept=".pdf,.jpg,.jpeg,.png"
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (!file) {
                  setSelectedFile(null);
                  return;
                }

                if (!ALLOWED_TYPES.includes(file.type)) {
                  message.error('Only PDF, JPG, and PNG files are allowed');
                  event.target.value = '';
                  return;
                }

                setSelectedFile(file);
              }}
            />
            <div style={{ marginTop: 8 }}>
              <Typography.Text type="secondary">
                {selectedFile ? `Selected: ${selectedFile.name}` : isEditMode ? 'Choose a new file to replace the current document.' : 'Select a PDF, JPG, or PNG file.'}
              </Typography.Text>
            </div>
          </Form.Item>
        </Form>
      </Modal>

      <Drawer
        title="Document Preview"
        placement="right"
        open={viewOpen}
        onClose={() => setViewOpen(false)}
        width={640}
      >
        {viewData && (
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <Card size="small">
              <p><strong>Name:</strong> {viewData.name}</p>
              <p><strong>Type:</strong> {fileLabel(viewData.type)}</p>
              <p><strong>Employee:</strong> {viewData.employee ? `${viewData.employee.firstName} ${viewData.employee.lastName}` : '-'}</p>
              <p><strong>Created At:</strong> {viewData.createdAt ? dayjs(viewData.createdAt).format('DD MMM YYYY HH:mm') : '-'}</p>
            </Card>

            {viewData.documentUrl && viewData.type === 'application/pdf' && (
              <iframe
                title="Document preview"
                src={viewData.documentUrl}
                style={{ width: '100%', height: 520, border: 0 }}
              />
            )}

            {viewData.documentUrl && viewData.type !== 'application/pdf' && (
              <img
                src={viewData.documentUrl}
                alt={viewData.name}
                style={{ width: '100%', borderRadius: 8 }}
              />
            )}
          </Space>
        )}
      </Drawer>
    </Layout>
  );
}

export default Documents;
